/**
 * ErrorPage Component
 *
 * A dedicated page for displaying access errors using Redux state.
 * This page receives error information through the ui-slice and displays
 * appropriate error messages and actions using the ErrorDisplay component.
 *
 * @module pages/ErrorPage
 */

import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';

import ErrorDisplay from 'components/common/errors/ErrorDisplay';
import { uiActions } from 'store/modules/ui-slice';

/**
 * ErrorPage component that displays error information from Redux state
 * @returns {JSX.Element}
 */
function ErrorPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const hasNavigatedAwayRef = useRef(false);

  // Theme-aware colors - ALL hooks must be called before any early returns
  const loadingTextColor = useColorModeValue('gray.600', 'gray.400');
  const loadingTextMain = useColorModeValue('gray.700', 'gray.200');
  const spinnerEmptyColor = useColorModeValue('gray.200', 'gray.600');

  // Get error state from Redux
  const errorState = useSelector((state) => state.ui.error);
  const { isActive, data, entityConfig, previousUrl } = errorState;

  // Check sessionStorage as fallback
  const sessionErrorState = sessionStorage.getItem('errorState');
  const fallbackError = sessionErrorState
    ? JSON.parse(sessionErrorState)
    : null;

  // Use Redux state or fallback to sessionStorage - memoized to prevent useEffect dependency changes
  const finalError = useMemo(
    () =>
      isActive && data
        ? { error: data, entityConfig, previousUrl }
        : fallbackError,
    [isActive, data, entityConfig, previousUrl, fallbackError]
  );

  // Initialize component and check for valid error state
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!finalError) {
        // No error state found anywhere, redirect to home
        navigate('/', { replace: true });
      } else {
        // Error state found, mark as initialized
        setIsInitialized(true);
      }
    }, 50); // Short timeout for immediate display

    return () => clearTimeout(timer);
  }, [finalError, navigate]);

  // Track navigation changes to detect when user leaves the error page
  useEffect(() => {
    return () => {
      // Only mark as navigated away when the location actually changes
      if (location.pathname !== '/error') {
        hasNavigatedAwayRef.current = true;
      }
    };
  }, [location.pathname]);

  // Clear error state only when actually navigating away from error page
  useEffect(() => {
    return () => {
      if (hasNavigatedAwayRef.current) {
        // Clear both Redux and sessionStorage when navigating away
        dispatch(uiActions.clearError());
        sessionStorage.removeItem('errorState');
      }
    };
  }, [dispatch]);

  // Show error content if we have valid error data and component is initialized
  if (finalError && isInitialized) {
    return (
      <ErrorDisplay
        error={finalError.error}
        entityConfig={finalError.entityConfig}
        previousUrl={finalError.previousUrl}
      />
    );
  }

  // Show loading state while checking for error data
  return (
    <Box textAlign="center" minH="100vh" py={20} px={6}>
      <VStack spacing={6} justify="center" minH="70vh">
        <Spinner
          size="xl"
          thickness="4px"
          speed="0.8s"
          emptyColor={spinnerEmptyColor}
          color="blue.500"
        />
        <VStack spacing={2}>
          <Text fontSize="lg" fontWeight="semibold" color={loadingTextMain}>
            Loading...
          </Text>
          <Text fontSize="sm" color={loadingTextColor}>
            Please wait while we prepare your error details
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
}

export default ErrorPage;
